import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { db } from '../services/firebase';

const ProjectAutocomplete = ({ companyId, userUid, onSelect, initialValue, isDisabled, label, onNameResolved }) => {
    const [projects, setProjects] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    
    // Speichert die zuletzt ausgewählte ID, um Änderungen zu erkennen
    const selectedIdRef = useRef(initialValue || '');
    const wrapperRef = useRef(null);

    // 1. Projekte laden, in denen der User Mitglied ist
    useEffect(() => {
        if (!userUid || !companyId) return; 
        const q = query(
            collection(db, "companies", companyId, "projects"),
            where("members", "array-contains", userUid)
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const loaded = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            loaded.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setProjects(loaded);
            setIsLoading(false);
        }, (error) => setIsLoading(false));
        return () => unsubscribe();
    }, [userUid, companyId]);

    // 2. Initialwert (ID) auflösen, falls vorhanden
    useEffect(() => {
        if (!initialValue) {
            // Wenn kein Initialwert, Feld leeren, wenn es nicht gerade bearbeitet wird
            if (!showSuggestions && inputValue !== '') { 
                setInputValue(''); 
                selectedIdRef.current = ''; 
            }
            return;
        }

        // Versuch, das Projekt in der bereits geladenen Liste zu finden
        const currentProject = projects.find(p => p.id === initialValue);
        
        if (currentProject) {
            // Projekt gefunden -> Name setzen
            if (inputValue !== currentProject.name) {
                setInputValue(currentProject.name);
                selectedIdRef.current = initialValue;
                if (onNameResolved) onNameResolved(currentProject.name);
            }
        } else if (companyId && initialValue && !isLoading) {
            // Projekt nicht in der Liste (vielleicht kein Mitglied mehr?), aber ID ist da.
            // Versuche Einzelabruf, um den Namen anzuzeigen.
            if (selectedIdRef.current !== initialValue) {
                const docRef = doc(db, "companies", companyId, "projects", initialValue);
                getDoc(docRef).then(snap => {
                     if (snap.exists()) {
                         const name = snap.data().name;
                         setInputValue(name);
                         selectedIdRef.current = initialValue;
                         if (onNameResolved) onNameResolved(name);
                     }
                 }).catch(e => console.error("Fehler beim Einzelabruf:", e.message));
            }
        }
    }, [initialValue, projects, companyId, isLoading, showSuggestions, inputValue, onNameResolved]); 

    // Klick außerhalb schließt Liste
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setShowSuggestions(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const commitSelection = (id, name) => {
        selectedIdRef.current = id;
        onSelect({ id, name });
        if (onNameResolved) onNameResolved(name);
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        setInputValue(val);
        setShowSuggestions(true);
        // Wenn leer, Auswahl löschen
        if (val === '') { commitSelection('', ''); return; }
    };

    const handleSelectProject = (project) => {
        setInputValue(project.name);
        setShowSuggestions(false);
        commitSelection(project.id, project.name);
    };

    const handleBlur = () => {
        // Kurze Verzögerung, damit Klick auf Liste registriert wird
        setTimeout(() => {
            // Wenn wir eine ID haben, setzen wir den Text auf den Namen zurück (validieren)
            // Oder wir lassen den Freitext stehen, wenn gewünscht. 
            // Hier: Wir erlauben nur echte Projekte oder leeren Input.
            // Falls man Freitext erlauben will (für Projekte die noch nicht existieren), müsste man das hier anpassen.
            // Aktuell: Reset auf letzten gültigen Wert oder leer.
            if (selectedIdRef.current) {
               // Alles gut
            } else { 
               // Kein Projekt gewählt -> Input löschen (Strict Mode)
               // setInputValue(''); 
               // commitSelection('', '');
            }
            setShowSuggestions(false);
        }, 200);
    };

    const filteredProjects = projects.filter(p => (p.name || '').toLowerCase().includes(inputValue.toLowerCase()));

    return (
        <div className="relative" ref={wrapperRef}>
            {label && <label className="block text-xs font-bold text-gray-500 uppercase mb-1">{label}</label>}
            <div className="relative">
                <input 
                    type="text" 
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    onFocus={() => !isDisabled && setShowSuggestions(true)}
                    placeholder={isLoading ? "Lade..." : "Projekt suchen..."}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:bg-gray-100 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                    disabled={isDisabled}
                    autoComplete="off"
                />
                {isLoading && (
                    <div className="absolute right-2 top-2">
                        <div className="animate-spin h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent"></div>
                    </div>
                )}
            </div>
            {showSuggestions && !isDisabled && (
                <ul className="absolute z-30 w-full bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg mt-1 max-h-60 overflow-y-auto shadow-xl">
                    {filteredProjects.length > 0 ? (
                        filteredProjects.map(p => (
                            <li key={p.id} onMouseDown={() => handleSelectProject(p)} className="px-3 py-2 hover:bg-indigo-50 dark:hover:bg-gray-600 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-0">
                                <div className="font-medium text-gray-800 dark:text-white text-sm">{p.name}</div>
                            </li>
                        ))
                    ) : (
                        <li className="px-3 py-2 text-gray-400 text-sm italic">Kein Projekt gefunden.</li>
                    )}
                </ul>
            )}
        </div>
    );
};

export default ProjectAutocomplete;